import { membersService } from "@/services";
import type { OrgRole, SeatStatus } from "@/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "./queryKeys";

export function useMembers(filters: { seatStatus?: SeatStatus } = {}) {
	return useQuery({
		queryKey: qk.members(filters),
		queryFn: ({ signal }) => membersService.list(filters, signal),
	});
}

/**
 * Roster mutations. Each invalidates BOTH the member list and the org — seat
 * counts live on `/api/org/me`, so a stale header after an invite would show
 * the wrong number of seats remaining.
 */
export function useMemberMutations() {
	const qc = useQueryClient();
	const invalidate = () => {
		qc.invalidateQueries({ queryKey: qk.members() });
		qc.invalidateQueries({ queryKey: qk.org });
	};

	return {
		invite: useMutation({
			mutationFn: ({
				emails,
				role,
			}: { emails: string[]; role?: Exclude<OrgRole, "owner"> }) =>
				membersService.invite(emails, role),
			onSuccess: invalidate,
		}),
		deactivate: useMutation({
			mutationFn: (memberId: string) => membersService.deactivate(memberId),
			onSuccess: invalidate,
		}),
		setRole: useMutation({
			mutationFn: ({ memberId, role }: { memberId: string; role: OrgRole }) =>
				membersService.setRole(memberId, role),
			onSuccess: invalidate,
		}),
	};
}
